import { GetServerSideProps } from 'next';

import { GeolinkInformationFieldsFragment, GeolinkPageSlugFieldsFragment } from 'api/graphql-types';
import { getServerPageGetGeoLink } from 'api/graphql-helper';
import client from 'api/apollo-client';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import { Head } from 'modules/head/head';
import IntroHeader, { IModule } from 'modules/intro-header/intro-header';
import { BaseModule } from 'modules/base-module/base-module';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import usePageProtection, { IUsePageProtectionVariables } from 'utils/hooks/use-page-protection';
import PasswordLayout from 'modules/password-layout';
import { buildPasswordProtectionStaticProps } from 'utils/password-protection-static';
import { logger, ErrorCode } from 'utils/logger';

interface ICatchAllPageProps extends IBasePageProps {
	page?: GeolinkPageSlugFieldsFragment;
	pageInformation?: GeolinkInformationFieldsFragment;
	id?: string;
	variables?: IUsePageProtectionVariables;
	isPasswordProtected?: boolean;
	passwordPageLayout?: any;
}

const CatchAllPage = (props: ICatchAllPageProps): React.JSX.Element => {
	const { page, PasswordModule } = usePageProtection({
		pageData: props.page || null,
		pageQuery: () => getServerPageGetGeoLink({ variables: props.variables }, client)
	});

	return (
		<Layout
			contentId={props?.id}
			typeName={props?.page?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
		>
			<Head seoModule={props?.page?.seo} seoParent={props?.pageInformation?.seo} />
			{page && page.__typename === 'GeolinkPage' ? (
				<>
					<BaseModule
						defaultModuleOptions={{
							backgroundColor: page.header?.backgroundColor || 'white',
							padding: 'none'
						}}
					>
						<IntroHeader module={page.header as IModule} slug={page.slug} showBreadCrumbs={false} />
					</BaseModule>
					<ModuleBuilder modules={page.modules || []} slug={page.slug} />
				</>
			) : props.isPasswordProtected ? (
				<PasswordLayout
					locale={props.variables?.locale}
					slug={props.variables?.slug}
					pageData={props.passwordPageLayout}
				>
					<PasswordModule />
				</PasswordLayout>
			) : null}
		</Layout>
	);
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
	try {
		const { locale, draftMode, publicationState } = getStrapiParametersFromContext(ctx);
		const slug = ctx.params?.slug as string[];
		const fullSlug = `/${slug.join('/')}`;
		const variables = { slug: fullSlug, locale, publicationState };

		const passwordResult = await buildPasswordProtectionStaticProps<any, any, any>({
			fullSlug,
			locale,
			publicationState,
			draftMode,
			contentTypeUid: 'api::geolink-page.geolink-page',
			contentTypeName: 'geolink-page',
			neutralStyle: false,
			hideLanguageSwitcher: true,
			hideSearchbar: true,
			isSubsite: true,
			fetchPage: () => getServerPageGetGeoLink({ variables }, client),
			extractInformation: (data) => data?.props?.data?.geolinkInformation?.data?.attributes,
			extractPage: (data) => {
				const node = data?.props?.data?.geolinkPageBySlug?.data;
				return node ? { documentId: node.documentId, attributes: node.attributes } : null;
			}
		});

		if (!passwordResult.isPasswordProtected && !passwordResult.page) {
			return { notFound: true };
		}

		return {
			props: {
				id: passwordResult.page?.documentId || null,
				variables,
				page: passwordResult.page,
				pageInformation: passwordResult.pageInformation,
				isPasswordProtected: passwordResult.isPasswordProtected,
				passwordPageLayout: passwordResult.passwordPageLayout,
				initialReduxState: passwordResult.initialReduxState
			}
		};
	} catch (error) {
		const slug = ctx.params?.slug as string[];
		const fullSlug = slug ? `/${slug.join('/')}` : 'unknown';
		const { locale } = getStrapiParametersFromContext(ctx);

		logger.error(
			'Failed to generate server-side props for Geolink page',
			error as Error,
			{
				slug: fullSlug,
				locale,
				params: ctx.params,
				component: 'getServerSideProps',
				site: 'geolink'
			},
			ErrorCode.PAGE_FETCH_ERROR
		);

		return { notFound: true };
	}
};

export default CatchAllPage;
